import {useEffect, useRef} from 'react';

type Options = {
    /** Called when camera permission transitions to 'granted'. */
    onCameraRestart?: () => void;
    /** Called when microphone permission transitions to 'granted'. */
    onMicRestart?: () => void;
};

const queryPermission = async (name: PermissionName): Promise<PermissionStatus | null> => {
    try {
        return await navigator.permissions.query({name});
    } catch {
        return null;
    }
};

/**
 * Provider-agnostic hook that fires restart callbacks whenever the browser
 * permissions for camera or microphone transition to 'granted'.
 *
 * Uses addEventListener (not .onchange) so it coexists safely with any other
 * code that also listens to the same PermissionStatus objects.
 *
 * @example
 * usePermissionDeviceRestart({
 *   onCameraRestart: () => restartMyCamera(),
 *   onMicRestart:    () => restartMyMic(),
 * });
 */
export const usePermissionDeviceRestart = ({onCameraRestart, onMicRestart}: Options) => {
    // Keep refs fresh so the effect closure never goes stale on re-renders
    const onCameraRestartRef = useRef(onCameraRestart);
    const onMicRestartRef = useRef(onMicRestart);
    onCameraRestartRef.current = onCameraRestart;
    onMicRestartRef.current = onMicRestart;

    useEffect(() => {
        let camStatus: PermissionStatus | null = null;
        let micStatus: PermissionStatus | null = null;
        let cancelled = false;

        const handleCamChange = () => {
            if (camStatus?.state === 'granted') {
                onCameraRestartRef.current?.();
            }
        };

        const handleMicChange = () => {
            if (micStatus?.state === 'granted') {
                onMicRestartRef.current?.();
            }
        };

        void (async () => {
            const [cam, mic] = await Promise.all([
                queryPermission('camera' as PermissionName),
                queryPermission('microphone' as PermissionName),
            ]);

            if (cancelled) return;

            camStatus = cam;
            micStatus = mic;
            camStatus?.addEventListener('change', handleCamChange);
            micStatus?.addEventListener('change', handleMicChange);
        })();

        return () => {
            cancelled = true;
            camStatus?.removeEventListener('change', handleCamChange);
            micStatus?.removeEventListener('change', handleMicChange);
        };
    }, []);
};
